import React from "react";
import styled from "styled-components";

import { AdminToolbar } from "@opendash/ui";
import { AdminLayout, LinkedSourcePicker } from "@opendash/core";
import { Parse } from "@openinc/parse-hooks";
import { Icon } from "@opendash/icons";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);
import {
  Alert,
  Button,
  Descriptions,
  Badge,
  Modal,
  Input,
  Statistic,
  Select,
  InputNumber,
  notification,
  Col,
  Popconfirm,
  Row,
  Result,
} from "antd";

import { useTranslation, copy } from "@opendash/core";

const ButtonGroup = styled.div`
  display: flex;
  margin-bottom: 24px;
  justify-content: flex-end;

  > button {
    margin-left: 8px;
  }
`;

const DebugBox = styled.div`
  max-height: 65vh;
  overflow: auto;
  font-size: 11px;
  font-family: monospace;
  white-space: pre;
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
`;

export default function GreisOperatingHoursSelect({
  spsid,
  admin,
  config,
  user,
  name,
}: {
  spsid: string;
  admin: boolean;
  config: any;
  user: any;
  name: string;
}) {
  let { result, loading, error } = config;

  const [parseObj, setparseObj] = React.useState(result);
  const t = useTranslation();

  const [data, setData] = React.useState<any[]>([]);
  const [userData, setUserData] = React.useState<any>();
  const [showDataNow, setShowDataNow] = React.useState(0);
  const [showDataNowDate, setShowDataDate] = React.useState(0);
  const [showData24, setShowData24] = React.useState(0);
  const [showData60, setShowData60] = React.useState(0);
  const [showData724, setShowData724] = React.useState(0);
  const [showData770, setShowData770] = React.useState(0);
  const [showWarning60, setShowWarning60] = React.useState(false);
  const [showWarning770, setShowWarning770] = React.useState(false);
  const showWarning = showWarning60 || showWarning770;

  const [opConfig, setOPConfig] = React.useState({});
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [deviationSet, setDeviationSet] = React.useState(false);

  const [hasError, setHasError] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const [actionType, setActionType] = React.useState<string | undefined>();
  const [difference, setDifference] = React.useState(0);
  const [userMail, setUserMail] = React.useState("");

  const [saveactionType, setSaveActionType] = React.useState<
    string | undefined
  >();
  const [savedifference, setSaveDifference] = React.useState(0);
  const [saveuserMail, setSaveUserMail] = React.useState("");

  const [configExist, setConfigExist] = React.useState(false);

  // Debug state — used by the "Debug API" button
  const [matchedItem, setMatchedItem] = React.useState<{
    dataItem: any;
    idx: number;
  } | null>(null);
  const [debugOpen, setDebugOpen] = React.useState(false);
  const [debugLoading, setDebugLoading] = React.useState(false);
  const [debugResult, setDebugResult] = React.useState<any>(null);

  if (!spsid) {
    return (
      <Alert
        type="info"
        message={t("greis:operatinghours.ui.select_location_first")}
      />
    );
  }

  let translateType = [
    t("greis:operatinghours.ui.daylie"),
    t("greis:operatinghours.ui.weekly"),
  ];

  React.useEffect(() => {
    setparseObj(result);
    const cfg = result && result.get("config");
    if (cfg && Object.keys(cfg).length != 0) {
      setOPConfig(copy(cfg));
      setConfigExist(true);
      setActive(copy(result.get("status")));
      setDifference(copy(cfg.diff));
      setUserMail(copy(cfg.mail));
      setActionType(copy(cfg.type));
    } else {
      setOPConfig({});
      setConfigExist(false);
      setActive(false);
    }
  }, [result]);

  let baseURL = "https://hgneu.openinc.de";
  //let baseURL = "https://schwarz.henrichgruppe.com";
  let itemURL = "/api/data/items";
  let historyURL = "/api/data/historical";

  React.useEffect(() => {
    if (user != userData) {
      setUserData(user);
    }
  }, [user]);

  React.useEffect(() => {
    let isCancelled = false;
    if (userData) {
      const getData = async (spsid: string) => {
        console.log("[GreisOperatingHours] Fetching data for spsid:", spsid);
        try {
          const response = await fetch(
            baseURL + itemURL + "/" + user.getUsername() + "/" + spsid,
            {
              method: "GET",
              headers: {
                "OD-Session": user.getSessionToken(),
              },
            },
          );

          if (!response.ok) {
            console.error(
              "[GreisOperatingHours] API Error:",
              response.status,
              response.statusText,
            );
            throw new Error("Bad response from backend");
          }

          const body = await response.json();
          console.log("[GreisOperatingHours] Data received:", body);
          if (!isCancelled) {
            setData(body);
          }
        } catch (error) {
          console.error("[GreisOperatingHours] Error fetching data:", error);
        }
      };

      getData(spsid);
    }
    return () => {
      isCancelled = true;
    };
  }, [userData]);

  React.useEffect(() => {
    if (data.length > 0) {
      console.log("[GreisOperatingHours] Processing data, items:", data.length);
      data.forEach((dataItem: any, dataIdx: number) => {
        if (!dataItem) {
          console.warn(
            "[GreisOperatingHours] Null dataItem at index:",
            dataIdx,
          );
          return;
        }
        const valueTypes =
          (dataItem?.["valueTypes"] as any[] | undefined) ?? [];
        console.log(
          "[GreisOperatingHours] Item",
          dataIdx,
          "valueTypes:",
          valueTypes,
        );
        const idx = valueTypes.findIndex(
          (valueType: any) =>
            valueType &&
            valueType["name"] &&
            (valueType["name"].includes("Betriebsstunden") ||
              valueType["name"].includes("Operating") ||
              valueType["name"].includes("Operational")),
        );
        if (idx >= 0) {
          const valueType = valueTypes[idx];
          console.log(
            "[GreisOperatingHours] Found Operating Hours:",
            valueType["name"],
            "at index:",
            idx,
          );
          setMatchedItem({ dataItem, idx });
          //Operating Hours
          const currentValue = dataItem.values?.[0]?.value?.[idx] ?? 0;
          const currentDate = dataItem.values?.[0]?.date;
          console.log(
            "[GreisOperatingHours] Current:",
            currentValue,
            "Date:",
            currentDate,
          );
          setShowDataNow(currentValue);
          setShowDataDate(currentDate);
          // Type 1: Gestern (Tag -1) - für Vergleich mit Tagesdurchschnitt
          getHistoryData(
            dataItem,
            dataItem["id"],
            dayjs(Date.now()).startOf("day").subtract(1, "d"),
            dayjs(Date.now()).startOf("day"),
            spsid,
            idx,
            1,
          ).catch(console.error);
          // Type 2: Letzte 60 Tage (Tag -61 bis -1) - für Tagesdurchschnitt
          getHistoryData(
            dataItem,
            dataItem["id"],
            dayjs(Date.now()).startOf("day").subtract(61, "d"),
            dayjs(Date.now()).startOf("day").subtract(1, "d"),
            spsid,
            idx,
            2,
          ).catch(console.error);
          // Type 3: Letzte volle Kalenderwoche (Vergleichswert)
          getHistoryData(
            dataItem,
            dataItem["id"],
            dayjs(Date.now()).startOf("isoWeek").subtract(7, "d"),
            dayjs(Date.now()).startOf("isoWeek"),
            spsid,
            idx,
            3,
          ).catch(console.error);

          // Type 4: 10 volle Wochen DAVOR (Baseline, schließt letzte Woche aus)
          getHistoryData(
            dataItem,
            dataItem["id"],
            dayjs(Date.now()).startOf("isoWeek").subtract(77, "d"),
            dayjs(Date.now()).startOf("isoWeek").subtract(7, "d"),
            spsid,
            idx,
            4,
          ).catch(console.error);
        }
      });
    }
  }, [data]);

  const getHistoryData = async (
    data: any,
    id: string,
    start: any,
    end: any,
    spsid: string,
    dimension: number,
    type: number,
  ) => {
    const startStr = String(dayjs(start).valueOf());
    const endStr = String(dayjs(end).valueOf());
    const url =
      baseURL +
      historyURL +
      "/" +
      spsid +
      "/" +
      id +
      "/" +
      startStr +
      "/" +
      endStr +
      "?mode=minmax&dimension=" +
      dimension +
      "&maxValues=10";

    console.log(
      `[GreisOperatingHours] ========== Type ${type} START ==========`,
    );
    console.log(`[GreisOperatingHours] Type ${type} - Zeitraum:`, {
      start: dayjs(start).format("DD.MM.YYYY HH:mm:ss"),
      end: dayjs(end).format("DD.MM.YYYY HH:mm:ss"),
      startTimestamp: startStr,
      endTimestamp: endStr,
      dimension,
    });
    console.log(`[GreisOperatingHours] Type ${type} - URL:`, url);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "OD-Session": user.getSessionToken(),
        },
      });

      if (!response.ok) {
        console.error(
          `[GreisOperatingHours] History API Error (Type ${type}):`,
          response.status,
        );
        throw new Error("Bad response from backend");
      }

      const body: any = await response.json();
      console.log(`[GreisOperatingHours] Type ${type} - Response:`, {
        valuesCount: body.values?.length || 0,
        fullResponse: body,
      });

      // mode=minmax kann den aktuellen Counter-Wert mit zurückgeben,
      // selbst wenn er außerhalb der Range liegt → max/min wird vergiftet.
      // Defensiv clientseitig auf [startTs, endTs) filtern.
      const startTs = Number(startStr);
      const endTs = Number(endStr);
      const beforeCount = body.values?.length ?? 0;
      body.values = (body.values ?? []).filter(
        (item: any) =>
          item?.date != null && item.date >= startTs && item.date < endTs,
      );
      const removed = beforeCount - body.values.length;
      if (removed > 0) {
        console.log(
          `[GreisOperatingHours] Type ${type} - filtered ${removed} sample(s) outside range`,
        );
      }

      if (type == 1) {
        // Type 1: Betriebsstunden von GESTERN (für Vergleich mit 60-Tage-Durchschnitt)
        console.log("[GreisOperatingHours] Type 1 - Berechnung START");
        if (!body.values || body.values.length === 0) {
          console.warn("[GreisOperatingHours] Type 1: No values!");
          console.log(
            "[GreisOperatingHours] ========== Type 1 END (NO DATA) ==========\n",
          );
          setShowData24(0);
          return;
        }
        console.log("[GreisOperatingHours] Type 1 - Raw values:", body.values);
        const values = body.values
          .map((item: any) => item?.value?.[dimension])
          .filter((v: any) => v != null);
        console.log(
          "[GreisOperatingHours] Type 1 - Extracted values (gestern):",
          values,
        );
        // Differenz zwischen max und min = Betriebsstunden des gestrigen Tages
        // KEINE Filterung von 0-Werten (wie im Java-Backend)
        const max = values.length > 0 ? Math.max(...values) : 0;
        const min = values.length > 0 ? Math.min(...values) : 0;
        let hoursYesterday = max - min;
        console.log("[GreisOperatingHours] Type 1 - Berechnung:", {
          max,
          min,
          hoursYesterday,
          formel: `${max} - ${min} = ${hoursYesterday}`,
        });
        console.log("[GreisOperatingHours] ========== Type 1 END ==========\n");
        setShowData24(hoursYesterday);
      } else if (type == 2) {
        // Type 2: Durchschnittliche Betriebsstunden pro Tag (letzte 60 Tage)
        console.log("[GreisOperatingHours] Type 2 - Berechnung START");
        if (!body.values || body.values.length === 0) {
          console.warn("[GreisOperatingHours] Type 2: No values!");
          console.log(
            "[GreisOperatingHours] ========== Type 2 END (NO DATA) ==========\n",
          );
          setShowData60(0);
          setShowWarning60(false);
          return;
        }
        console.log("[GreisOperatingHours] Type 2 - Raw values:", body.values);

        // Sortiere die Werte nach Datum (wie im Java-Backend)
        const sortedValues = [...body.values].sort(
          (a: any, b: any) => a.date - b.date,
        );

        const values = sortedValues
          .map((item: any) => item?.value?.[dimension])
          .filter((v: any) => v != null);

        console.log("[GreisOperatingHours] Type 2 - Extracted values:", {
          allValues: values,
        });

        // KEINE Filterung von 0-Werten (wie im Java-Backend)
        const max = values.length > 0 ? Math.max(...values) : -1;
        const min = values.length > 0 ? Math.min(...values) : -1;

        // Berechne tatsächliche Anzahl der Tage aus den Daten
        let firstDay = 0;
        let lastDay = 0;

        if (sortedValues.length > 0) {
          firstDay = sortedValues[0].date;
          lastDay = sortedValues[sortedValues.length - 1].date;
        }

        let days = 60;
        let calcDays = Math.round((lastDay - firstDay) / 1000 / 60 / 60 / 24);

        if (calcDays < 57) {
          days = calcDays;
        }

        let totalHours = max - min;
        // Verwende Math.round statt Math.floor (wie im Java-Backend)
        // Guard gegen Division durch 0 (alle Werte am selben Tag)
        let avgHoursPerDay = days > 0 ? Math.round(totalHours / days) : 0;

        const hasZero = values.length > 0 && Math.min(...values) === 0;
        console.log("[GreisOperatingHours] Type 2 - Berechnung:", {
          max,
          min,
          totalHours,
          avgHoursPerDay,
          hasZero,
          calcDays,
          usedDays: days,
          formel: `(${max} - ${min}) / ${days} = ${totalHours} / ${days} = ${avgHoursPerDay}`,
        });
        console.log("[GreisOperatingHours] ========== Type 2 END ==========\n");
        setShowWarning60(hasZero);
        setShowData60(avgHoursPerDay);
      } else if (type == 3) {
        // Type 3: Betriebsstunden der letzten 7 Tage (für Vergleich mit 10-Wochen-Durchschnitt)
        console.log("[GreisOperatingHours] Type 3 - Berechnung START");
        if (!body.values || body.values.length === 0) {
          console.warn("[GreisOperatingHours] Type 3: No values!");
          console.log(
            "[GreisOperatingHours] ========== Type 3 END (NO DATA) ==========\n",
          );
          setShowData724(0);
          return;
        }
        console.log("[GreisOperatingHours] Type 3 - Raw values:", body.values);
        const values = body.values
          .map((item: any) => item?.value?.[dimension])
          .filter((v: any) => v != null);
        console.log(
          "[GreisOperatingHours] Type 3 - Extracted values (letzte 7 Tage):",
          values,
        );
        // Differenz zwischen max und min = Betriebsstunden der letzten 7 Tage
        // KEINE Filterung von 0-Werten (wie im Java-Backend)
        const max = values.length > 0 ? Math.max(...values) : 0;
        const min = values.length > 0 ? Math.min(...values) : 0;
        let hoursLast7Days = max - min;
        console.log("[GreisOperatingHours] Type 3 - Berechnung:", {
          max,
          min,
          hoursLast7Days,
          formel: `${max} - ${min} = ${hoursLast7Days}`,
        });
        console.log("[GreisOperatingHours] ========== Type 3 END ==========\n");
        setShowData724(hoursLast7Days);
      } else if (type == 4) {
        // Type 4: Durchschnittliche Betriebsstunden pro Woche (letzte 10 Wochen)
        console.log("[GreisOperatingHours] Type 4 - Berechnung START");
        if (!body.values || body.values.length === 0) {
          console.warn("[GreisOperatingHours] Type 4: No values!");
          console.log(
            "[GreisOperatingHours] ========== Type 4 END (NO DATA) ==========\n",
          );
          setShowData770(0);
          setShowWarning770(false);
          return;
        }
        console.log("[GreisOperatingHours] Type 4 - Raw values:", body.values);

        // Sortiere die Werte nach Datum (wie im Java-Backend)
        const sortedValues = [...body.values].sort(
          (a: any, b: any) => a.date - b.date,
        );

        const values = sortedValues
          .map((item: any) => item?.value?.[dimension])
          .filter((v: any) => v != null);

        console.log("[GreisOperatingHours] Type 4 - Extracted values:", {
          allValues: values,
        });

        // KEINE Filterung von 0-Werten (wie im Java-Backend)
        const max = values.length > 0 ? Math.max(...values) : -1;
        const min = values.length > 0 ? Math.min(...values) : -1;

        // Berechne tatsächliche Anzahl der Wochen aus den Daten
        let firstDay = 0;
        let lastDay = 0;

        if (sortedValues.length > 0) {
          firstDay = sortedValues[0].date;
          lastDay = sortedValues[sortedValues.length - 1].date;
        }

        let weeks = 10;
        let calcWeeks = Math.round(
          (lastDay - firstDay) / 1000 / 60 / 60 / 24 / 7,
        );

        if (calcWeeks < 7) {
          weeks = calcWeeks;
        }

        let totalHours = max - min;
        // Verwende Math.round statt Math.floor (wie im Java-Backend)
        // Guard gegen Division durch 0 (alle Werte in derselben Woche)
        let avgHoursPerWeek = weeks > 0 ? Math.round(totalHours / weeks) : 0;

        const hasZero = values.length > 0 && Math.min(...values) === 0;
        console.log("[GreisOperatingHours] Type 4 - Berechnung:", {
          max,
          min,
          totalHours,
          avgHoursPerWeek,
          hasZero,
          calcWeeks,
          usedWeeks: weeks,
          formel: `(${max} - ${min}) / ${weeks} = ${totalHours} / ${weeks} = ${avgHoursPerWeek}`,
        });
        console.log("[GreisOperatingHours] ========== Type 4 END ==========\n");
        setShowWarning770(hasZero);
        setShowData770(avgHoursPerWeek);
      }
    } catch (error) {
      console.error(
        `[GreisOperatingHours] Error in getHistoryData (type ${type}):`,
        error,
      );
    }
  };

  const openNotificationWithIcon = (
    type: "info" | "success" | "warning" | "error",
    title: string,
    message: string,
  ) => {
    notification[type]({
      message: title,
      description: message,
    });
  };

  const showModal = () => {
    console.log("[GreisOperatingHours] showModal - State:", {
      actionType,
      difference,
      userMail,
      showData24,
      showData60,
      showData724,
      showData770,
    });

    try {
      setSaveActionType(copy(actionType));
      setSaveDifference(copy(difference));
      setSaveUserMail(copy(userMail));
    } catch (e) {
      console.error("[GreisOperatingHours] Error copying config:", e);
    }

    if (difference > 0) {
      if (actionType == "1") {
        console.log("[GreisOperatingHours] Checking Type 1 (Daily)");
        let upperCorner = showData60 + showData60 * (difference / 100);
        let lowerCorner = showData60 - showData60 * (difference / 100);
        console.log(
          "[GreisOperatingHours] Type 1 Range:",
          lowerCorner,
          "-",
          upperCorner,
          "Value:",
          showData24,
        );
        if (showData24 > upperCorner || showData24 < lowerCorner) {
          console.log("[GreisOperatingHours] Type 1 DEVIATION!");
          setDeviationSet(true);
        } else {
          setDeviationSet(false);
        }
      } else if (actionType == "2") {
        console.log("[GreisOperatingHours] Checking Type 2 (Weekly)");
        let upperCorner = showData770 + showData770 * (difference / 100);
        let lowerCorner = showData770 - showData770 * (difference / 100);
        console.log(
          "[GreisOperatingHours] Type 2 Range:",
          lowerCorner,
          "-",
          upperCorner,
          "Value:",
          showData724,
        );
        if (showData724 > upperCorner || showData724 < lowerCorner) {
          console.log("[GreisOperatingHours] Type 2 DEVIATION!");
          setDeviationSet(true);
        } else {
          setDeviationSet(false);
        }
      }
    }

    setIsModalOpen(true);
  };

  const delConfig = () => {
    try {
      parseObj.destroy().then(
        (e: any) => {
          setparseObj(null);
          setOPConfig({});
          setConfigExist(false);
          setActive(false);
          setActionType(undefined);
          setDifference(0);
          setUserMail("");
          setSaveActionType(undefined);
          setSaveDifference(0);
          setSaveUserMail("");
          console.log(parseObj);
        },
        (error: any) => {
          console.log("Parse Error deleting Object...");
        },
      );
      openNotificationWithIcon(
        "info",
        t("greis:operatinghours.ui.config_deleted"),
        t("greis:operatinghours.ui.config_deleted_text"),
      );
    } catch (e) {
      console.log("Delete Error....", e);
    }
  };

  const setActiveStatus = (e: boolean) => {
    console.log(result);
    parseObj.set("status", e);
    parseObj.save().then((f: any) => {
      setOPConfig(copy(parseObj.get("config")));
      setConfigExist(true);
      setActive(e);
      setActionType(copy(actionType));
      setDifference(copy(difference));
      setUserMail(copy(userMail));
      setparseObj(f);
      if (e) {
        openNotificationWithIcon(
          "info",
          t("greis:operatinghours.ui.monitoring_active"),
          t("greis:operatinghours.ui.monitoring_active_text"),
        );
      } else {
        openNotificationWithIcon(
          "info",
          t("greis:operatinghours.ui.monitoring_not_active"),
          t("greis:operatinghours.ui.monitoring_not_active_text"),
        );
      }
    });
  };

  const saveConfig = () => {
    const configObject = {
      type: saveactionType,
      mail: saveuserMail,
      diff: savedifference,
    };

    if (parseObj) {
      parseObj.set("spsid", spsid);
      parseObj.set("status", active);
      parseObj.set("config", configObject);
      parseObj.save().then((e: any) => {
        setOPConfig(copy(parseObj.get("config")));
        setConfigExist(true);
        setActionType(copy(saveactionType));
        setDifference(copy(savedifference));
        setUserMail(copy(saveuserMail));
        openNotificationWithIcon(
          "success",
          t("greis:operatinghours.ui.config_saved"),
          t("greis:operatinghours.ui.config_saved_text"),
        );
        setparseObj(e);
      });
    } else {
      setActive(true);
      const OService = Parse.Object.extend("HG_Runtime");
      const newObj = new OService();
      newObj.set("spsid", spsid);
      newObj.set("status", true);
      newObj.set("config", configObject);

      const acl = new Parse.ACL(user);
      acl.setReadAccess(user, true);
      acl.setWriteAccess(user, true);
      acl.setReadAccess("role:od-admin", true);
      acl.setWriteAccess("role:od-admin", true);
      acl.setPublicWriteAccess(false);
      acl.setPublicReadAccess(false);

      newObj.setACL(acl);

      newObj.save().then((e: any) => {
        setOPConfig(copy(newObj.get("config")));
        setConfigExist(true);
        setActionType(copy(saveactionType));
        setDifference(copy(savedifference));
        setUserMail(copy(saveuserMail));
        openNotificationWithIcon(
          "success",
          t("greis:operatinghours.ui.config_saved"),
          t("greis:operatinghours.ui.config_saved_text"),
        );
        setparseObj(e);
      });
    }
  };

  const changeNumber = (number: number, actionTyper?: string) => {
    console.log("[GreisOperatingHours] changeNumber:", { number, actionTyper });

    if (number > 0 && actionTyper) {
      if (actionTyper === "1") {
        let upperCorner = showData60 + showData60 * (number / 100);
        let lowerCorner = showData60 - showData60 * (number / 100);
        const isDeviation =
          showData24 > upperCorner || showData24 < lowerCorner;

        console.log("[GreisOperatingHours] changeNumber Type 1:", {
          upperCorner,
          lowerCorner,
          showData24,
          isDeviation,
        });

        setDeviationSet(isDeviation);
      } else if (actionTyper === "2") {
        let upperCorner = showData770 + showData770 * (number / 100);
        let lowerCorner = showData770 - showData770 * (number / 100);
        const isDeviation =
          showData724 > upperCorner || showData724 < lowerCorner;

        console.log("[GreisOperatingHours] changeNumber Type 2:", {
          upperCorner,
          lowerCorner,
          showData724,
          isDeviation,
        });

        setDeviationSet(isDeviation);
      }
    } else {
      console.log("[GreisOperatingHours] changeNumber skipped (invalid input)");
    }
  };

  const handleOk = () => {
    if (
      saveactionType &&
      Number(saveactionType) > 0 &&
      saveuserMail.length > 4
    ) {
      //Save Values in Parse
      saveConfig();
      setIsModalOpen(false);
    } else {
      setHasError(true);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Debug helper: prüft ob eine Zahlenreihe monoton wachsend ist (für Counter-Sanity-Check)
  const checkMonotonic = (samples: { date: number; v: number }[]): string => {
    if (samples.length < 2) return "n/a (zu wenige Samples)";
    const sorted = [...samples].sort((a, b) => a.date - b.date);
    let increasing = true;
    let decreasing = true;
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].v < sorted[i - 1].v) increasing = false;
      if (sorted[i].v > sorted[i - 1].v) decreasing = false;
    }
    if (increasing) return "monoton steigend ✓ (Counter-Verhalten)";
    if (decreasing) return "monoton fallend ⚠";
    return "NICHT MONOTON ⚠ (kein klassischer Counter)";
  };

  // Debug-Button: ruft alle 4 Zeitfenster OHNE mode=minmax & ohne maxValues ab
  const runDebug = async () => {
    if (!matchedItem) return;
    setDebugLoading(true);
    setDebugOpen(true);
    setDebugResult(null);

    const { dataItem, idx } = matchedItem;
    const ranges = [
      {
        name: "Type 1 — gestern (1d)",
        start: dayjs(Date.now()).startOf("day").subtract(1, "d"),
        end: dayjs(Date.now()).startOf("day"),
      },
      {
        name: "Type 2 — 60d Baseline",
        start: dayjs(Date.now()).startOf("day").subtract(61, "d"),
        end: dayjs(Date.now()).startOf("day").subtract(1, "d"),
      },
      {
        name: "Type 3 — letzte volle Woche",
        start: dayjs(Date.now()).startOf("isoWeek").subtract(7, "d"),
        end: dayjs(Date.now()).startOf("isoWeek"),
      },
      {
        name: "Type 4 — 10w Baseline (vor letzter Woche)",
        start: dayjs(Date.now()).startOf("isoWeek").subtract(77, "d"),
        end: dayjs(Date.now()).startOf("isoWeek").subtract(7, "d"),
      },
    ];

    const results: any = {
      matchedValueType: dataItem.valueTypes?.[idx]?.name,
      dimensionIndex: idx,
      spsid,
      dataItemId: dataItem.id,
      apiVariant: "OHNE mode=minmax und ohne maxValues",
      ranges: {},
    };

    for (const range of ranges) {
      const startStr = String(dayjs(range.start).valueOf());
      const endStr = String(dayjs(range.end).valueOf());
      const url = `${baseURL}${historyURL}/${spsid}/${dataItem.id}/${startStr}/${endStr}?dimension=${idx}`;
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { "OD-Session": user.getSessionToken() },
        });
        if (!response.ok) {
          results.ranges[range.name] = {
            url,
            error: `HTTP ${response.status} ${response.statusText}`,
          };
          continue;
        }
        const body: any = await response.json();
        const rawSamples: { date: number; v: number }[] = (body.values ?? [])
          .map((item: any) => ({
            date: item?.date,
            v: item?.value?.[idx],
          }))
          .filter((s: any) => s.date != null && s.v != null);
        const sortedSamples = [...rawSamples].sort((a, b) => a.date - b.date);
        const vals = sortedSamples.map((s) => s.v);
        results.ranges[range.name] = {
          url,
          rangeStart: dayjs(range.start).format("YYYY-MM-DD HH:mm"),
          rangeEnd: dayjs(range.end).format("YYYY-MM-DD HH:mm"),
          count: sortedSamples.length,
          min: vals.length > 0 ? Math.min(...vals) : null,
          max: vals.length > 0 ? Math.max(...vals) : null,
          maxMinusMin:
            vals.length > 0 ? Math.max(...vals) - Math.min(...vals) : null,
          monotonic: checkMonotonic(sortedSamples),
          firstFive: sortedSamples.slice(0, 5).map((s) => ({
            date: dayjs(s.date).format("YYYY-MM-DD HH:mm"),
            v: s.v,
          })),
          lastFive: sortedSamples.slice(-5).map((s) => ({
            date: dayjs(s.date).format("YYYY-MM-DD HH:mm"),
            v: s.v,
          })),
        };
      } catch (e: any) {
        results.ranges[range.name] = { url, error: String(e) };
      }
    }

    console.log("[GreisOperatingHours DEBUG]", results);
    setDebugResult(results);
    setDebugLoading(false);
  };

  let getAction = (admin: boolean, active: boolean, configExist: boolean) => {
    let actions = [];
    if (admin && !active) {
      if (spsid && !spsid.includes("tenant")) {
        actions.push(
          <Button
            disabled={!configExist}
            type="primary"
            icon={<Icon icon="fa:check" />}
            children={t("greis:operatinghours.ui.button_activate")}
            onClick={() => {
              setActiveStatus(true);
            }}
          />,
        );
      }
    } else if (admin && active) {
      if (spsid && !spsid.includes("tenant")) {
        actions.push(
          <Button
            danger
            type="dashed"
            icon={<Icon icon="fa:pause" />}
            children={t("greis:operatinghours.ui.button_deactivate")}
            onClick={() => {
              setActiveStatus(false);
            }}
          />,
        );
      }
    }

    if (admin) {
      if (spsid && !spsid.includes("tenant")) {
        actions.push(
          <Button
            icon={<Icon icon="fa:pen" />}
            onClick={showModal}
            children={t("greis:operatinghours.ui.button_edit")}
          />,
        );
      }
    }

    if (admin && configExist) {
      actions.push(
        <Popconfirm
          title={t("greis:operatinghours.ui.button_delete_text")}
          onConfirm={delConfig}
          okText={t("greis:operatinghours.ui.button_delete_yes")}
          cancelText={t("greis:operatinghours.ui.button_delete_no")}
        >
          <Button
            danger
            icon={<Icon icon="fa:trash" />}
            children={t("greis:operatinghours.ui.button_delete")}
          />
        </Popconfirm>,
      );
    }

    if (admin && matchedItem) {
      actions.push(
        <Button
          icon={<Icon icon="fa:bug" />}
          onClick={runDebug}
          children="Debug API"
        />,
      );
    }

    return actions;
  };

  return (
    <AdminLayout>
      <AdminToolbar
        title={t("greis:operatinghours.label")}
        description={t("greis:operatinghours.description")}
        actions={getAction(admin, active, configExist)}
      />
      <div className="od-page-main">
        {spsid && spsid.includes("tenant") && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh", // Mindesthöhe, um den Inhalt zu zentrieren
            }}
          >
            <Result
              icon={
                <Icon
                  icon="fa:circle"
                  style={{ color: "rgb(254, 192, 0)", fontSize: "48px" }}
                />
              }
              title={t("greis:manualinput.component.no_support")}
              extra={
                <div>
                  <p style={{ marginBottom: "8px", fontSize: "16px" }}>
                    {t("greis:manualinput.component.select_location")}
                  </p>
                  <LinkedSourcePicker style={{ width: "400px" }} />
                </div>
              }
            />
          </div>
        )}

        {spsid && !spsid.includes("tenant") && (
          <>
            <Modal
              title="API Debug-Output (ohne mode=minmax)"
              open={debugOpen}
              onCancel={() => setDebugOpen(false)}
              footer={[
                <Button
                  key="copy"
                  onClick={() => {
                    if (debugResult) {
                      navigator.clipboard.writeText(
                        JSON.stringify(debugResult, null, 2),
                      );
                      openNotificationWithIcon(
                        "success",
                        "Kopiert",
                        "Debug-JSON in Zwischenablage.",
                      );
                    }
                  }}
                >
                  In Zwischenablage kopieren
                </Button>,
                <Button
                  key="close"
                  type="primary"
                  onClick={() => setDebugOpen(false)}
                >
                  Schließen
                </Button>,
              ]}
              width={900}
            >
              <DebugBox>
                {debugLoading
                  ? "Lade Debug-Daten..."
                  : debugResult
                    ? JSON.stringify(debugResult, null, 2)
                    : "—"}
              </DebugBox>
            </Modal>
            <Modal
              title={t("greis:operatinghours.ui.modal_title")}
              open={isModalOpen}
              onOk={handleOk}
              onCancel={handleCancel}
              footer={[
                <Button key="back" onClick={handleCancel}>
                  {t("greis:operatinghours.ui.modal_cancel")}
                </Button>,
                <Button key="submit" type="primary" onClick={handleOk}>
                  {t("greis:operatinghours.ui.modal_save")}
                </Button>,
              ]}
            >
              <p>
                <Select
                  placeholder={t("greis:operatinghours.ui.modal_placeholder")}
                  style={{ width: "100%" }}
                  value={saveactionType}
                  onChange={(val) => {
                    setSaveActionType(val as any);
                    setHasError(false);
                    changeNumber(savedifference, val as any);
                  }}
                  options={[
                    {
                      value: "1",
                      label: t("greis:operatinghours.ui.modal_daylie"),
                    },
                    {
                      value: "2",
                      label: t("greis:operatinghours.ui.modal_weekly"),
                    },
                  ]}
                />
              </p>
              <p>
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder={t("greis:operatinghours.ui.modal_diff")}
                  min={0}
                  max={1000}
                  value={savedifference}
                  onChange={(e) => {
                    const val = e ?? 0;
                    changeNumber(val as number, saveactionType);
                    setSaveDifference(val as number);
                  }}
                  formatter={(value) => `${value}%`}
                />
              </p>
              <p>
                <Input
                  placeholder={t("greis:operatinghours.ui.modal_mail")}
                  value={saveuserMail}
                  onChange={(e) => {
                    setSaveUserMail(e.target.value);
                    setHasError(false);
                  }}
                  prefix={
                    <Icon icon="fa:envelope" style={{ marginRight: "5px" }} />
                  }
                />
              </p>

              <p>
                {saveactionType == "1" && (
                  <Alert
                    message={t("greis:operatinghours.ui.modal_actionday_text")}
                    type="info"
                  />
                )}
                {saveactionType == "2" && (
                  <Alert
                    message={t("greis:operatinghours.ui.modal_actionweek_text")}
                    type="info"
                  />
                )}
                {deviationSet && (
                  <div>
                    <br />
                    <Alert
                      message={t("greis:operatinghours.ui.modal_hint")}
                      type="warning"
                    />
                  </div>
                )}
              </p>

              <p>
                {hasError && (
                  <Alert
                    message={t("greis:operatinghours.ui.modal_error")}
                    type="error"
                  />
                )}
              </p>
            </Modal>
            <Row>
              <Col md={24} xl={24}>
                {!active && configExist && (
                  <Alert
                    message={t("greis:operatinghours.ui.noti_deactive")}
                    type="error"
                  />
                )}
              </Col>
            </Row>
            <Descriptions bordered column={3}>
              <Descriptions.Item
                label={t("greis:operatinghours.ui.table_source")}
                span={3}
              >
                <b>
                  {name} | {spsid}{" "}
                </b>
              </Descriptions.Item>
              <Descriptions.Item
                label={t("greis:operatinghours.ui.table_ophours")}
                span={3}
              >
                {showDataNow} {t("greis:operatinghours.ui.table_details_hours")}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("greis:operatinghours.ui.table_date")}
                span={3}
              >
                {dayjs(showDataNowDate).year() >= 2000
                  ? `${dayjs(showDataNowDate).format("DD.MM.YYYY")} | ${dayjs(
                      showDataNowDate,
                    ).format("HH:mm:ss")}`
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("greis:operatinghours.ui.table_status")}
                span={3}
              >
                {active && <Badge status="success" text="Status" />}
                {!active && <Badge status="error" text="Status" />}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  t("greis:operatinghours.ui.table_details") +
                  " (" +
                  t("greis:operatinghours.ui.table_details_hours") +
                  ")"
                }
                span={3}
              >
                <Row gutter={14}>
                  <Col span={10} style={{ borderRight: "1px solid lightgray" }}>
                    <Statistic
                      title={t(
                        "greis:operatinghours.ui.table_details_yesterday",
                      )}
                      value={showData24}
                      valueStyle={{
                        color: showData24 >= showData60 ? "#3f8600" : "#cf1322",
                      }}
                      prefix={
                        showData24 >= showData60 ? (
                          <Icon icon="fa:arrow-up" />
                        ) : (
                          <Icon icon="fa:arrow-down" />
                        )
                      }
                      style={{ textAlign: "center" }}
                    />
                  </Col>
                  <Col span={10}>
                    <Statistic
                      title={t(
                        "greis:operatinghours.ui.table_details_lastweek",
                      )}
                      value={showData724}
                      valueStyle={{
                        color:
                          showData724 >= showData770 ? "#3f8600" : "#cf1322",
                      }}
                      prefix={
                        showData724 >= showData770 ? (
                          <Icon icon="fa:arrow-up" />
                        ) : (
                          <Icon icon="fa:arrow-down" />
                        )
                      }
                      style={{ textAlign: "center" }}
                    />
                  </Col>
                </Row>
                <br />
                <Row gutter={14}>
                  <Col span={10} style={{ borderRight: "1px solid lightgray" }}>
                    <Statistic
                      title={t("greis:operatinghours.ui.table_details_60d")}
                      value={showData60}
                      style={{ textAlign: "center" }}
                      prefix={<Icon icon="fa:arrow-right" />}
                    />
                  </Col>
                  <Col span={10}>
                    <Statistic
                      title={t(
                        "greis:operatinghours.ui.table_details_last10week",
                      )}
                      value={showData770}
                      style={{ textAlign: "center" }}
                      prefix={<Icon icon="fa:arrow-right" />}
                    />
                  </Col>
                </Row>
              </Descriptions.Item>
              {showWarning && (
                <Descriptions.Item
                  label={t("greis:operatinghours.ui.table_warning")}
                  span={3}
                >
                  <b style={{ color: "#c43737" }}>
                    {t("greis:operatinghours.ui.table_warnung_text")}
                  </b>
                </Descriptions.Item>
              )}
              {configExist && (
                <Descriptions.Item
                  label={t("greis:operatinghours.ui.table_config")}
                  span={3}
                >
                  {t("greis:operatinghours.ui.table_config_diff")}: {difference}
                  %
                  <br />
                  {t("greis:operatinghours.ui.table_config_mail")}: {userMail}
                  <br />
                  {t("greis:operatinghours.ui.table_config_periode")}:{" "}
                  {(() => {
                    const idx = actionType ? Number(actionType) - 1 : -1;
                    return idx >= 0 && idx < translateType.length
                      ? translateType[idx]
                      : "-";
                  })()}
                </Descriptions.Item>
              )}
              {!configExist && (
                <Descriptions.Item
                  label={t("greis:operatinghours.ui.table_config")}
                  span={3}
                >
                  <b>{t("greis:operatinghours.ui.table_no_config")}</b>
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
